import { useState } from 'react'
import {
  downloadDocument,
  useChecklistHistory,
  useFolders,
  useGroups,
  useMembers,
  useSetChecklistAssignees,
  useSetChecklistNotes,
  useUpload,
} from '../api/rooms'
import { ApiError } from '../api/client'
import type {
  ChecklistItemResponse,
  ChecklistPrincipal,
  ChecklistSectionResponse,
  RoomResponse,
} from '../api/types'
import { DATA_POLICY_FIELD } from '../api/types'
import { isTerminalStatus } from '../lib/checklist'
import { formatDateOnly } from '../lib/checklistView'
import { errorMessage, formatUtc } from '../lib/format'
import { UploadDropzone } from './UploadDropzone'

/*
  Phase 2 of the request list inside an opened request (ADR 0009): when it is wanted and by
  whom, the answer when the answer is words, who owes it, a way to upload straight into it, and
  what has happened to it. Each part is drawn from what the server said this caller may do, and
  the server decides again on the click.
*/

/** Dates, the related request and the form to fill in. Nothing here is editable: the owner sets them on the item's form. */
export function ItemFacts({
  roomId,
  item,
  onOpenRelated,
}: {
  roomId: string
  item: ChecklistItemResponse
  onOpenRelated?: (itemId: string) => void
}) {
  const { requestedOn, goalDate, relatedItem, formDocument } = item
  const [formError, setFormError] = useState<string | null>(null)
  if (!requestedOn && !goalDate && !relatedItem && !formDocument && !item.assignedToMe) return null
  return (
    <ul className="plain-list item-facts small">
      {item.assignedToMe && (
        <li>
          <span className="chip">Assigned to you</span>
        </li>
      )}
      {requestedOn && <li>Requested {formatDateOnly(requestedOn)}</li>}
      {goalDate && (
        <li className={item.isOverdue ? 'overdue' : undefined}>
          Goal date {formatDateOnly(goalDate)}
          {item.isOverdue && ' · overdue'}
        </li>
      )}
      {relatedItem && (
        <li>
          See also{' '}
          <button
            type="button"
            className="link-btn"
            onClick={() => onOpenRelated?.(relatedItem.id)}
          >
            {relatedItem.docLabel === null
              ? relatedItem.title
              : `${relatedItem.docLabel} ${relatedItem.title}`}
          </button>
        </li>
      )}
      {formDocument && (
        <li>
          {/* An ordinary room document through the ordinary download route: the download bit and the watermark apply. */}
          Form to fill in:{' '}
          <button
            type="button"
            className="link-btn"
            onClick={() =>
              downloadDocument(roomId, formDocument.id).catch((e: unknown) =>
                setFormError(errorMessage(e)),
              )
            }
          >
            {formDocument.name}
          </button>
          {formError && <span className="error-text"> {formError}</span>}
        </li>
      )}
    </ul>
  )
}

/**
 * The responding side's answer in words. For some requests ("confirm whether...") this IS the
 * deliverable, so it is not tucked away.
 *
 * NO AUTOSAVE ON KEYSTROKE (ADR 0009 s4): two people typing into one request under
 * last-write-wins would destroy each other's words silently. It saves on the button, naming the
 * notes it read; if someone else wrote since, the API answers 409, NOTHING IS OVERWRITTEN, the
 * list reloads, and this box keeps what the person typed beside what the other wrote.
 */
export function ResponderNotes({
  roomId,
  room,
  item,
  canSubmit,
}: {
  roomId: string
  room: RoomResponse
  item: ChecklistItemResponse
  canSubmit: boolean
}) {
  const save = useSetChecklistNotes(roomId)
  const saved = item.responderNotes ?? ''
  const [draft, setDraft] = useState(saved)
  const [conflict, setConflict] = useState(false)
  const editable = canSubmit && room.status === 'Open' && !isTerminalStatus(item.status)
  const dirty = draft !== saved

  if (!editable && !saved) return null

  const submit = () => {
    setConflict(false)
    save.mutate(
      {
        itemId: item.id,
        responderNotes: draft.trim() === '' ? null : draft,
        expectedNotesUtc: item.responderNotesUtc ?? null,
      },
      {
        onError: (e) => {
          if (e instanceof ApiError && e.status === 409 && 'expectedNotesUtc' in e.violations)
            setConflict(true)
        },
      },
    )
  }

  const policyRefusal = save.error instanceof ApiError && DATA_POLICY_FIELD in save.error.violations

  return (
    <section className="note note-responder" aria-label={`Answer in words to ${item.title}`}>
      <p className="note-label">
        Answer in words
        {item.responderNotesUtc && (
          <span className="muted">
            {' '}
            · last written{item.responderNotesBy ? ` by ${item.responderNotesBy}` : ''}{' '}
            {formatUtc(item.responderNotesUtc)}
          </span>
        )}
      </p>
      {editable ? (
        <>
          <textarea
            className="request-input"
            rows={4}
            maxLength={20000}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Answer in words"
            placeholder="Where the answer is a statement rather than a file, write it here. It can be submitted with no document."
          />
          {conflict && (
            <div className="alert alert-error" role="alert">
              <p className="small no-margin">
                Someone else saved an answer while you were writing. Yours was not saved and theirs
                was not overwritten. Theirs now reads:
              </p>
              <pre className="request-text request-text-note">{saved || '(empty)'}</pre>
              <p className="small no-margin">
                Put the two together in the box above and save again.
              </p>
            </div>
          )}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!dirty || save.isPending}
              onClick={submit}
            >
              Save the answer
            </button>
            {dirty && !save.isPending && <span className="small muted">Not saved yet.</span>}
            {save.isSuccess && !dirty && <span className="small muted">Saved.</span>}
            {save.isError && !conflict && (
              <span className="error-text small">
                {policyRefusal
                  ? 'Accept the privacy and data use policy at the top of this page first.'
                  : errorMessage(save.error)}
              </span>
            )}
          </div>
        </>
      ) : (
        <pre className="request-text request-text-note">{saved}</pre>
      )}
    </section>
  )
}

/**
 * Who owes the answer. Shown, and editable, only to someone who may already see the room's
 * members (an owner or manage_permissions): a room may hold competing bidders, and a name here
 * says who else is in it (ADR 0009 s5). Everyone else sees "Assigned to you", or nothing.
 */
export function ItemAssignees({
  roomId,
  item,
  editable,
}: {
  roomId: string
  item: ChecklistItemResponse
  editable: boolean
}) {
  const [editing, setEditing] = useState(false)
  const members = useMembers(roomId, editing)
  const groups = useGroups(roomId, editing)
  const set = useSetChecklistAssignees(roomId)
  const current = item.assignees ?? []
  const [picked, setPicked] = useState<ChecklistPrincipal[]>([])
  if (item.assignees === null || item.assignees === undefined) return null

  const key = (p: ChecklistPrincipal) => `${p.type}:${p.id}`
  const toggle = (p: ChecklistPrincipal) =>
    setPicked((now) =>
      now.some((x) => key(x) === key(p)) ? now.filter((x) => key(x) !== key(p)) : [...now, p],
    )
  const isPicked = (p: ChecklistPrincipal) => picked.some((x) => key(x) === key(p))

  return (
    <section aria-label={`Who owes an answer to ${item.title}`}>
      <h4 className="h4">Assigned to</h4>
      {current.length === 0 && !editing && <p className="muted small no-margin">Nobody yet.</p>}
      {!editing && current.length > 0 && (
        <div className="chips">
          {current.map((a) => (
            <span
              key={`${a.type}:${a.id}`}
              className={a.cannotSubmit ? 'chip chip-warn' : 'chip'}
              title={
                a.cannotSubmit
                  ? 'Holds no upload permission in this room, so cannot submit an answer. Assigning is not granting.'
                  : undefined
              }
            >
              {a.name ?? a.id}
              {a.type === 'Group' && ' (group)'}
              {a.cannotSubmit && ' · cannot submit'}
            </span>
          ))}
        </div>
      )}
      {editable && !editing && (
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => {
            setPicked(current.map((a) => ({ type: a.type, id: a.id })))
            setEditing(true)
          }}
        >
          Change
        </button>
      )}
      {editing && (
        <form
          className="stack-sm"
          onSubmit={(e) => {
            e.preventDefault()
            set.mutate(
              { itemId: item.id, assignees: picked },
              { onSuccess: () => setEditing(false) },
            )
          }}
        >
          <p className="small muted no-margin">
            Assigning tells the person and shows the request under their open items. It grants
            nothing: what they may see and upload is set under Permissions.
          </p>
          {(members.isPending || groups.isPending) && <p className="muted small">Loading…</p>}
          <fieldset className="checklist-picker">
            <legend className="small muted">People</legend>
            <div className="chips">
              {(members.data ?? []).map((m) => {
                const p: ChecklistPrincipal = { type: 'User', id: m.userId }
                return (
                  <label
                    key={m.userId}
                    className={`chip chip-pick${isPicked(p) ? ' chip-picked' : ''}`}
                  >
                    <input type="checkbox" checked={isPicked(p)} onChange={() => toggle(p)} />{' '}
                    {m.displayName ?? m.email ?? m.userId}
                  </label>
                )
              })}
            </div>
          </fieldset>
          {(groups.data ?? []).length > 0 && (
            <fieldset className="checklist-picker">
              <legend className="small muted">Groups</legend>
              <div className="chips">
                {(groups.data ?? []).map((g) => {
                  const p: ChecklistPrincipal = { type: 'Group', id: g.id }
                  return (
                    <label
                      key={g.id}
                      className={`chip chip-pick${isPicked(p) ? ' chip-picked' : ''}`}
                    >
                      <input type="checkbox" checked={isPicked(p)} onChange={() => toggle(p)} />{' '}
                      {g.name}
                    </label>
                  )
                })}
              </div>
            </fieldset>
          )}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary btn-sm" disabled={set.isPending}>
              Save
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setEditing(false)}>
              Cancel
            </button>
            {set.isError && <span className="error-text small">{errorMessage(set.error)}</span>}
          </div>
        </form>
      )}
    </section>
  )
}

/**
 * Upload straight into a request (ADR 0009 s11): the existing upload route with this item's id,
 * into the section's linked folder when the caller may upload there, else the room's root when
 * they may upload there, else not offered at all. Nothing new on the server.
 */
export function ItemUpload({
  roomId,
  room,
  item,
  section,
}: {
  roomId: string
  room: RoomResponse
  item: ChecklistItemResponse
  section: ChecklistSectionResponse | null
}) {
  const folders = useFolders(roomId)
  const upload = useUpload(roomId)
  const [log, setLog] = useState<{ name: string; ok: boolean; result: string }[]>([])
  if (room.status !== 'Open' || isTerminalStatus(item.status)) return null

  const linked = (folders.data ?? []).find((f) => f.id === section?.vdrFolderId)
  const target = linked?.permissions.upload
    ? { id: linked.id, name: `${linked.indexNumber} ${linked.name}` }
    : room.permissions.upload
      ? { id: null, name: 'the room’s top level' }
      : null
  if (!target) return null

  const onFiles = async (files: File[]) => {
    for (const file of files) {
      try {
        const r = await upload.mutateAsync({ folderId: target.id, file, itemIds: [item.id] })
        setLog((l) => [
          { name: file.name, ok: r.scanStatus === 'Clean', result: r.scanStatus },
          ...l,
        ])
      } catch (e) {
        setLog((l) => [{ name: file.name, ok: false, result: errorMessage(e) }, ...l])
      }
    }
  }

  return (
    <section aria-label={`Upload an answer to ${item.title}`}>
      <p className="small muted no-margin">
        Files dropped here go into {target.name} and answer this request.
      </p>
      <UploadDropzone disabled={false} busy={upload.isPending} onFiles={(f) => void onFiles(f)} />
      {log.length > 0 && (
        <ul className="plain-list small upload-log">
          {log.map((l, i) => (
            <li key={`${l.name}-${i}`} className={l.ok ? undefined : 'error-text'}>
              {l.name}: {l.result}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** What has happened to this request. An audited read, so it is fetched only when asked for. */
export function ItemHistory({ roomId, item }: { roomId: string; item: ChecklistItemResponse }) {
  const [open, setOpen] = useState(false)
  const history = useChecklistHistory(roomId, item.id, open)
  return (
    <section aria-label={`History of ${item.title}`}>
      <button
        type="button"
        className="btn btn-sm"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {open ? 'Hide history' : 'History'}
      </button>
      {open && history.isPending && <p className="muted small">Loading…</p>}
      {open && history.isError && <p className="error-text small">{errorMessage(history.error)}</p>}
      {open && history.data && history.data.length === 0 && (
        <p className="muted small no-margin">Nothing recorded yet.</p>
      )}
      {open && history.data && history.data.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>Who</th>
              <th>What</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {history.data.map((h, i) => (
              <tr key={`${h.eventUtc}-${i}`}>
                <td className="small">{formatUtc(h.eventUtc)}</td>
                <td className="small">{h.actor ?? '—'}</td>
                <td className="small mono">{h.action}</td>
                <td className="small">{h.outcome}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
